import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import TriageLayout from "./TriageLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MagnifyingGlass, UserPlus, PencilSimple, FilePdf, Broadcast, Trash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { isSupervisorOrAbove } from "@/lib/permissions";
import { fmtDateTimeET } from "@/lib/etTime";

const TAG_COLORS = {
  green:  { label: "Green",   bg: "bg-emerald-500 text-white" },
  yellow: { label: "Yellow",  bg: "bg-yellow-400 text-slate-900" },
  orange: { label: "Orange",  bg: "bg-orange-500 text-white" },
  red:    { label: "Red",     bg: "bg-red-600 text-white" },
  black:  { label: "Black",   bg: "bg-slate-900 text-white" },
};

function fmtDT(iso) {
  // Delegated to shared ET/24h helper so list rows match Command Post
  // and PDF paperwork.
  return fmtDateTimeET(iso);
}

export default function TriagePatientList() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canDelete = isSupervisorOrAbove(user);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  // IDs of rows added/updated in the current session — used to briefly
  // pulse the row so the operator can spot the new one at a glance.
  const [pulseIds, setPulseIds] = useState({});
  const loadRef = useRef(null);

  useEffect(() => {
    async function load() {
      try {
        const r = await api.get("/triage/patients");
        setRows(r.data?.patients || []);
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    }
    loadRef.current = load;
    load();
  }, []);

  // Live-update via the same notifications WebSocket the Command Post uses.
  // A new/updated record just triggers a refetch — cheap enough given the
  // page already lists <=500 rows and the WS payload is trimmed.
  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backend.replace(/^http/, "ws") + "/api/notifications/ws";
    let ws;
    try { ws = new WebSocket(wsUrl); } catch { return undefined; }
    ws.onopen = () => setWsConnected(true);
    ws.onclose = () => setWsConnected(false);
    ws.onerror = () => setWsConnected(false);
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg?.event === "triage.patient_created" || msg?.event === "triage.patient_updated") {
          const id = msg?.data?.id;
          if (id) {
            // Mark the row for a short highlight — clears itself after 8s.
            setPulseIds((prev) => ({ ...prev, [id]: Date.now() }));
            setTimeout(() => setPulseIds((prev) => {
              const next = { ...prev };
              delete next[id];
              return next;
            }), 8000);
          }
          if (loadRef.current) loadRef.current();
        }
        if (msg?.event === "triage.patient_deleted") {
          // Remove the row locally so the UI updates without a full refetch
          // (the WS payload is trimmed and doesn't include the record).
          const id = msg?.data?.id;
          if (id) setRows((prev) => prev.filter((r) => r.id !== id));
        }
      } catch { /* ignore keepalives */ }
    };
    return () => { try { ws.close(); } catch { /* noop */ } };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const hay = [r.first_name, r.last_name, r.event_name, r.triage_center, r.reason_for_treatment, r.created_by_name].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q]);

  async function downloadPdf(row) {
    try {
      const resp = await api.get(`/triage/patients/${row.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `triage_${(row.last_name || "patient").replace(/\s+/g, "_")}_${row.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "PDF export failed");
    }
  }

  async function deleteRow(row) {
    const label = `${row.first_name || ""} ${row.last_name || ""}`.trim() || "this record";
    if (!window.confirm(`Delete triage record for ${label}?`)) return;
    try {
      await api.delete(`/triage/patients/${row.id}`);
      // Remove locally in case the WS event beats or trails our own request.
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      // 5-second Undo toast — restore endpoint clears the soft-delete marker.
      toast(`Deleted "${label}"`, {
        description: "This record was moved to the trash. Tap Undo to restore.",
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/triage/patients/${row.id}/restore`);
              // WS broadcast will re-add the row; do a local refetch too in
              // case the socket is briefly disconnected.
              if (loadRef.current) loadRef.current();
              toast.success("Record restored");
            } catch (err) {
              toast.error(err.response?.data?.detail || "Restore failed");
            }
          },
        },
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <TriageLayout showBack>
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="font-display text-2xl font-black tracking-tight text-slate-900">Patient Records</h1>
            <p className="text-slate-500 text-sm flex items-center gap-2">
              {loading ? "Loading…" : `${filtered.length} record${filtered.length === 1 ? "" : "s"}`}
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold tracking-widest rounded-sm px-1.5 py-0.5 ${wsConnected ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`}
                    data-testid="tri-list-live-status">
                <Broadcast size={10} weight="bold" />
                {wsConnected ? "LIVE" : "OFFLINE"}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={14} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, event…" className="rounded-sm pl-7 w-64" data-testid="tri-list-search" />
            </div>
            <Button className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" onClick={() => nav("/triage/patient/new")} data-testid="tri-list-new">
              <UserPlus size={14} weight="bold" className="mr-1" /> New Patient
            </Button>
          </div>
        </div>

        <div className="surface-grid bg-white" data-testid="tri-list-grid">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left tab-tag text-slate-500">
                <th className="px-4 py-2">PCR #</th>
                <th className="px-4 py-2">Patient</th>
                <th className="px-4 py-2">Event</th>
                <th className="px-4 py-2">Tag</th>
                <th className="px-4 py-2">Reason</th>
                <th className="px-4 py-2">Provider</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Created</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-slate-500 text-center">No patient records yet.</td></tr>
              ) : filtered.map((r) => {
                const tag = TAG_COLORS[r.triage_tag_color] || null;
                const pulse = Boolean(pulseIds[r.id]);
                return (
                  <tr key={r.id} className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${pulse ? "bg-amber-50 tri-row-pulse" : ""}`} data-testid={`tri-row-${r.id}`}
                      onClick={() => nav(`/triage/patient/${r.id}/edit`)}>
                    <td className="px-4 py-2 mono text-xs text-slate-600">{r.pcr_number || "—"}</td>
                    <td className="px-4 py-2 font-medium text-slate-900">{r.first_name} {r.last_name}</td>
                    <td className="px-4 py-2 mono text-xs">{r.event_name || "—"}</td>
                    <td className="px-4 py-2">{tag ? <Badge className={`rounded-sm text-[10px] ${tag.bg}`}>{tag.label}</Badge> : "—"}</td>
                    <td className="px-4 py-2 mono text-xs">{r.reason_for_treatment || "—"}</td>
                    <td className="px-4 py-2 text-xs">{r.provider_name || r.created_by_name || "—"}</td>
                    <td className="px-4 py-2 tab-tag">
                      {r.status === "draft"
                        ? <span className="text-amber-700">DRAFT</span>
                        : <span className="text-emerald-700">SUBMITTED</span>}
                    </td>
                    <td className="px-4 py-2 mono text-xs">{fmtDT(r.created_at)}</td>
                    <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="sm" variant="outline" className="rounded-sm h-7 px-2"
                          onClick={() => nav(`/triage/patient/${r.id}/edit`)} data-testid={`tri-edit-${r.id}`} title="Edit / continue">
                          <PencilSimple size={12} weight="bold" />
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-sm h-7 px-2"
                          onClick={() => downloadPdf(r)} data-testid={`tri-pdf-${r.id}`} title="Export PDF">
                          <FilePdf size={12} weight="bold" />
                        </Button>
                        {canDelete && (
                          <Button size="sm" variant="outline" className="rounded-sm h-7 px-2 text-red-600 hover:bg-red-50 hover:border-red-300"
                            onClick={() => deleteRow(r)} data-testid={`tri-delete-${r.id}`} title="Delete record (Supervisor+)">
                            <Trash size={12} weight="bold" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </TriageLayout>
  );
}
